import { z } from 'zod';

/* ───────────────────── ДДС проекта по неделям и месяцам (ФН-01, раздел 6.14) ───────────────────── */

/* ───────────────────── Кассовый разрыв и его покрытие (ФН-14) ───────────────────── */

export const CashCoverKind = z.enum(['credit', 'guarantee', 'shift', 'own']);
export type CashCoverKind = z.infer<typeof CashCoverKind>;
export const CASH_COVER_KINDS: Record<CashCoverKind, string> = {
  credit: 'Кредит',
  guarantee: 'Гарантия',
  shift: 'Перенос платежей',
  own: 'Средства компании',
};

export const CashCoverInput = z
  .object({
    projectId: z.uuid().nullable().optional(),
    kind: CashCoverKind,
    amount: z.number().positive().max(1e13),
    dateFrom: z.iso.date(),
    dateTo: z.iso.date(),
    /** Годовая ставка, %: стоимость = сумма × ставка × дни / 365 */
    ratePercent: z.number().min(0).max(100).default(0),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Конец покрытия раньше начала', path: ['dateTo'] });
export type CashCoverInput = z.infer<typeof CashCoverInput>;

export const CashCover = z.object({
  id: z.uuid(),
  projectId: z.uuid().nullable(),
  kind: CashCoverKind,
  amount: z.number(),
  dateFrom: z.iso.date(),
  dateTo: z.iso.date(),
  ratePercent: z.number(),
  note: z.string().nullable(),
  /** Стоимость финансирования за срок покрытия */
  cost: z.number(),
});
export type CashCover = z.infer<typeof CashCover>;

export const CashGap = z.object({
  start: z.iso.date(),
  end: z.iso.date(),
  /** Наибольшая нехватка денег внутри разрыва */
  deficit: z.number(),
  /** Покрыто (не больше нехватки) и не покрыто */
  covered: z.number(),
  uncovered: z.number(),
  /** Стоимость финансирования покрытий, начатых в этом разрыве */
  cost: z.number(),
  covers: z.array(CashCover),
});
export type CashGap = z.infer<typeof CashGap>;

export const CashflowQuery = z.object({
  projectId: z.uuid().optional(),
  grain: z.enum(['week', 'month']).default('week'),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Остаток денег на начало показанного периода — для поиска кассовых разрывов (по умолчанию 0) */
  opening: z.coerce.number().optional(),
});
export type CashflowQuery = z.infer<typeof CashflowQuery>;

/** Источники плановых платежей ДДС (ФН-01, ФН-07) */
export const CASHFLOW_OUT_SOURCES = {
  contracts: 'Договоры и заявки на оплату',
  payroll: 'Персонал (по ФП)',
  admin: 'Административные расходы (по ФП)',
  tax: 'Налоги и обязательные платежи (по ФП)',
  guarantees: 'Комиссии банковских гарантий и аккредитивов',
} as const;
export type CashflowOutSource = keyof typeof CASHFLOW_OUT_SOURCES;

export const CashflowPeriod = z.object({
  start: z.iso.date(),
  end: z.iso.date(),
  /** Поступления: по плану (ещё не получено) и фактические */
  inPlanned: z.number(),
  inPaid: z.number(),
  /** Платежи: по плану (не оплачено, в том числе в заявках) и фактические */
  outPlanned: z.number(),
  outPaid: z.number(),
  /** Итог периода: поступления минус платежи, план и факт вместе */
  net: z.number(),
  /** Нарастающий итог с начала показанного периода (остаток на начало не известен и не учитывается) */
  cumulative: z.number(),
  /** Плановые платежи периода по источникам: договоры и заявки, статьи ФП, комиссии гарантий */
  outBySource: z.object({
    contracts: z.number(),
    payroll: z.number(),
    admin: z.number(),
    tax: z.number(),
    guarantees: z.number(),
  }),
});
export type CashflowPeriod = z.infer<typeof CashflowPeriod>;

export const Cashflow = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  grain: z.enum(['week', 'month']),
  projectId: z.uuid().nullable(),
  periods: z.array(CashflowPeriod),
  /** План без даты (по поставке без графика поставок и т. п.) */
  undated: z.object({ inPlanned: z.number(), outPlanned: z.number() }),
  /** План с датой раньше или позже показанных периодов (просроченный и дальний) */
  outside: z.object({ inPlanned: z.number(), outPlanned: z.number() }),
  totals: z.object({
    inPlanned: z.number(),
    inPaid: z.number(),
    outPlanned: z.number(),
    outPaid: z.number(),
  }),
  /** Кассовые разрывы (ФН-14): периоды, где остаток уходит ниже нуля, с покрытием и стоимостью */
  gaps: z.array(CashGap),
  /** Все покрытия разрывов, попавшие в показанный период */
  covers: z.array(CashCover),
  /** Может ли смотрящий вносить и убирать покрытия (финансовый менеджер компании) */
  canCover: z.boolean(),
  /** Плановые платежи за показанный период по источникам (см. outBySource у периода) */
  outBySource: z.object({
    contracts: z.number(),
    payroll: z.number(),
    admin: z.number(),
    tax: z.number(),
    guarantees: z.number(),
  }),
  /** Утверждённые ФП, из которых взяты персонал, административные расходы и налоги */
  plans: z.array(z.object({ projectId: z.uuid(), projectCode: z.string(), number: z.number().int() })),
  /** Проекты без утверждённого ФП: их персонала и налогов в ДДС нет */
  withoutPlan: z.array(z.object({ projectId: z.uuid(), projectCode: z.string() })),
  /** Статьи ДДС, для которых в портале пока нет источника данных */
  notCovered: z.array(z.string()),
});
export type Cashflow = z.infer<typeof Cashflow>;
